import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Confetti from "react-confetti";
import { SpinAWheelApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { ExternalLink } from "lucide-react";

const SEGMENT_COLORS = [
  "#fee2e2", "#dbeafe", "#dcfce7", "#fef3c7", "#ede9fe",
  "#fce7f3", "#e0e7ff", "#fef9c3", "#d1fae5", "#f3e8ff",
  "#ffe4e6", "#e0f2fe", "#f0fdf4", "#fefce8", "#fdf4ff"
];

interface SpinWheelSlot {
  id: number;
  slotName: string;
  points: number;
  spinConfigId: number;
}

interface SpinWheelConfig {
  id: number;
  spinName: string;
  startDate: string;
  endDate: string;
  noOfSlots: number;
  spinPerMember: number;
  bonusValidityInDay: number;
  nonWinningSlots: number;
  spinAWheelSlots: SpinWheelSlot[];
}

export function GamificationSpinWheel() {
  const { toast } = useToast();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });
  const [isCollecting, setIsCollecting] = useState(false);
  const [mustCollectPoints, setMustCollectPoints] = useState(false);
  const [spinWheelConfigs, setSpinWheelConfigs] = useState<SpinWheelConfig[]>([]);
  const [selectedSpinWheelId, setSelectedSpinWheelId] = useState<string>("");

  // Get selected spin wheel config
  const selectedSpinWheel = useMemo(() => {
    return spinWheelConfigs.find(config => String(config.id) === selectedSpinWheelId);
  }, [spinWheelConfigs, selectedSpinWheelId]);

  // Check if selected spin wheel has slots
  const hasSlots = useMemo(() => {
    return selectedSpinWheel && 
           selectedSpinWheel.spinAWheelSlots && 
           selectedSpinWheel.spinAWheelSlots.length > 0;
  }, [selectedSpinWheel]);

  // Generate segments from selected spin wheel config
  const SEGMENTS = useMemo(() => {
    if (!selectedSpinWheel || !hasSlots) {
      return [0]; // Default to a single 0 segment if no config selected or no slots
    }
    // Use the spinAWheelSlots array directly - it already contains all slots with their points
    // Sort by slot name or keep original order, and extract points
    const points = selectedSpinWheel.spinAWheelSlots.map(slot => slot.points);
    
    // If we have fewer slots than noOfSlots, add non-winning slots (0 points) to match
    const totalSlots = selectedSpinWheel.noOfSlots;
    const currentSlots = points.length;
    if (currentSlots < totalSlots) {
      const additionalSlots = totalSlots - currentSlots;
      const nonWinningSlots = Array(additionalSlots).fill(0);
      return [...points, ...nonWinningSlots];
    }
    
    return points;
  }, [selectedSpinWheel, hasSlots]);

  const segmentAngle = 360 / SEGMENTS.length;

  const paths = useMemo(() => {
    const radius = 150;
    const center = 0;
    return SEGMENTS.map((_, index) => {
      const startAngle = ((index * segmentAngle - 90) * Math.PI) / 180;
      const endAngle = (((index + 1) * segmentAngle - 90) * Math.PI) / 180;

      const x1 = center + radius * Math.cos(startAngle);
      const y1 = center + radius * Math.sin(startAngle);
      const x2 = center + radius * Math.cos(endAngle);
      const y2 = center + radius * Math.sin(endAngle);

      const largeArcFlag = segmentAngle > 180 ? 1 : 0;

      return {
        d: `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`,
      };
    });
  }, [segmentAngle, SEGMENTS.length]);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window !== "undefined") {
        setConfettiSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Fetch spin wheel configs on mount
  useEffect(() => {
    fetchSpinWheelConfigs();
  }, []);

  const fetchSpinWheelConfigs = async () => {
    try {
      const response = await SpinAWheelApi.getAllSpinAwheelConfig({});
      
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        const configs = (response.getSpinAwheelConfig || []).map((config: any) => ({
          id: config.id || 0,
          spinName: config.spinName || "",
          startDate: config.startDate || "",
          endDate: config.endDate || "",
          noOfSlots: config.noOfSlots || 0,
          spinPerMember: config.spinPerMember || 0,
          bonusValidityInDay: config.bonusValidityInDay || 0,
          nonWinningSlots: config.nonWinningSlots || 0,
          spinAWheelSlots: config.spinAWheelSlots || [],
        }));
        setSpinWheelConfigs(configs);
      }
    } catch (error: any) {
      console.error("Error fetching spin wheel configs:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch spin wheel configurations",
        variant: "destructive",
      });
    }
  };

  const handleSendOtp = () => {
    if (!mobile || mobile.length < 7 || mobile.length > 15) {
      toast({
        title: "Invalid mobile number",
        description: "Please enter a valid mobile number (7-15 digits).",
        variant: "destructive",
      });
      return;
    }
    setOtpSent(true);
    setIsOtpVerified(false);
    setOtp("");
    toast({
      title: "OTP sent",
      description: "A test OTP has been 'sent' to the entered mobile number.",
    });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid OTP.",
        variant: "destructive",
      });
      return;
    }

    if (otp !== "1992") {
      toast({
        title: "Incorrect OTP",
        description: "Please enter the correct OTP.",
        variant: "destructive",
      });
      setIsOtpVerified(false);
      return;
    }

    setIsOtpVerified(true);
    toast({
      title: "OTP verified",
      description: "You can now spin the wheel.",
    });
  };

  const handleSpin = () => {
    if (!isOtpVerified || isSpinning || mustCollectPoints || !selectedSpinWheel) return;

    const extraSpins = 10; // extra full rotations for animation (more visible spinning)
    const randomOffset = Math.random() * 360; // add randomness within one rotation
    const finalRotation = rotation + extraSpins * 360 + randomOffset;

    setIsSpinning(true);
    setRotation(finalRotation);
    setSelectedIndex(null);
    setMustCollectPoints(false);

    setTimeout(() => {
      setIsSpinning(false);

      // Determine which segment is at the pointer after the spin
      const pointerAngle = 180; // left pointer
      const normalizedRotation = ((finalRotation % 360) + 360) % 360; // 0–360

      let winningIndex = 0;
      let minDiff = Infinity;

      SEGMENTS.forEach((_, index) => {
        // Logical center angle of this segment (same as drawing logic)
        const centerAngle = -90 + (index + 0.5) * segmentAngle;
        // Where that center ends up on screen after rotation
        const screenAngle =
          (((centerAngle + normalizedRotation) % 360) + 360) % 360;
        const rawDiff = Math.abs(screenAngle - pointerAngle);
        const diff = Math.min(rawDiff, 360 - rawDiff);

        if (diff < minDiff) {
          minDiff = diff;
          winningIndex = index;
        }
      });

      setSelectedIndex(winningIndex);
      const pointsWon = SEGMENTS[winningIndex];
      if (pointsWon > 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 8000);
      }
      // Require user to collect points even if 0, so that every spin is recorded
      setMustCollectPoints(true);
      toast({
        title: "Spin Result",
        description: `You won ${SEGMENTS[winningIndex]} points!`,
      });
    }, 6000);
  };

  const handleCollectPoints = async () => {
    if (selectedIndex === null || !selectedSpinWheel) {
      return;
    }

    const pointsWon = SEGMENTS[selectedIndex];

    if (!mobile || !isOtpVerified) {
      toast({
        title: "Verification required",
        description: "Please verify your mobile number and OTP before collecting points.",
        variant: "destructive",
      });
      return;
    }

    setIsCollecting(true);
    // User has acknowledged collecting; allow next spin regardless of API outcome
    setMustCollectPoints(false);
    try {
      const payload = {
        p_MobileNo: mobile,
        p_OTP: "1992", // using the same test OTP for now
        p_PointsWon: pointsWon,
        p_SpinId: selectedSpinWheel.id, // Use selected spin wheel ID
      };

      const response = await SpinAWheelApi.insertSpinAWheelTransaction(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Points Collected",
          description: response.message || "Points collected successfully.",
        });
        // Reset verification, mobile, and result after successful collection
        setIsOtpVerified(false);
        setOtpSent(false);
        setOtp("");
        setMobile("");
        setSelectedIndex(null);
      }
    } catch (error: any) {
      console.error("Error collecting points:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to collect points.",
        variant: "destructive",
      });
    } finally {
      setIsCollecting(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {showConfetti && confettiSize.width > 0 && confettiSize.height > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={300}
          recycle={false}
        />
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">Spin a Wheel</h2>
          <p className="text-sm text-muted-foreground">
            Verify your mobile number and spin the wheel to win points.
          </p>
        </div>
        <Button variant="outline" size="sm" className="w-fit shrink-0" asChild>
          <a
            href="/spin-wheel"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Public page
          </a>
        </Button>
      </div>

      {/* Mobile & OTP Section */}
      <Card>
        <CardHeader>
          <CardTitle>Mobile Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mobile Number</label>
            <div className="flex gap-2">
              <Input
                type="tel"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobile(value);
                }}
                maxLength={15}
              />
              <Button type="button" variant="outline" onClick={handleSendOtp}>
                Send OTP
              </Button>
            </div>
          </div>

          {otpSent && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Enter OTP</label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setOtp(value);
                  }}
                  maxLength={6}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyOtp}
                >
                  Verify
                </Button>
              </div>
              {isOtpVerified && (
                <p className="text-xs text-emerald-600 font-medium">
                  OTP verified. You can spin the wheel now.
                </p>
              )}
            </div>
          )}

          {isOtpVerified && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Spin Wheel *</label>
              <Select
                value={selectedSpinWheelId}
                onValueChange={(value) => {
                  setSelectedSpinWheelId(value);
                  // Reset spin state when changing wheel
                  setSelectedIndex(null);
                  setRotation(0);
                  setMustCollectPoints(false);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a spin wheel" />
                </SelectTrigger>
                <SelectContent>
                  {spinWheelConfigs.map((config) => (
                    <SelectItem key={config.id} value={String(config.id)}>
                      {config.spinName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedSpinWheelId && (
                <p className="text-xs text-gray-500">
                  Please select a spin wheel to continue.
                </p>
              )}
              {selectedSpinWheel && !hasSlots && (
                <p className="text-xs text-red-600 font-medium">
                  ⚠️ Slots are not configured for this spin wheel. Please configure slots before spinning.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wheel & Result Section */}
      <div className="grid gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-96 h-96 flex items-center justify-center">
            {!selectedSpinWheel ? (
              <div className="w-80 h-80 rounded-full border-4 border-gray-200 shadow-md flex items-center justify-center bg-gray-50">
                <p className="text-sm text-gray-500 text-center px-4">
                  Please select a spin wheel after OTP verification
                </p>
              </div>
            ) : !hasSlots ? (
              <div className="w-80 h-80 rounded-full border-4 border-red-200 shadow-md flex items-center justify-center bg-red-50">
                <div className="text-center px-4">
                  <p className="text-sm font-medium text-red-600 mb-1">
                    ⚠️ Slots Not Configured
                  </p>
                  <p className="text-xs text-red-500">
                    This spin wheel does not have any slots configured. Please configure slots before spinning.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Wheel */}
                <div
                  className="w-80 h-80 rounded-full border-4 border-gray-200 shadow-md flex items-center justify-center"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: "transform 6s cubic-bezier(0.15, 0.85, 0.25, 1)",
                  }}
                >
                  <svg
                    width="320"
                    height="320"
                    viewBox="-160 -160 320 320"
                    className="overflow-visible"
                  >
                    {paths.map((path, index) => (
                      <path
                        key={index}
                        d={path.d}
                        fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                        stroke="#e5e7eb"
                        strokeWidth="1"
                      />
                    ))}
                    {SEGMENTS.map((points, index) => {
                      const angle =
                        ((index + 0.5) * segmentAngle * Math.PI) / 180 -
                        Math.PI / 2;
                      const labelRadius = 100;
                      const x = labelRadius * Math.cos(angle);
                      const y = labelRadius * Math.sin(angle);
                      // Get slot name if available - match by index or find by points
                      const slot = selectedSpinWheel?.spinAWheelSlots?.[index];
                      const slotName = slot?.slotName || "";
                      return (
                        <text
                          key={index}
                          x={x}
                          y={y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-semibold fill-gray-800"
                        >
                          {points > 0 ? `${points} pts` : "0"}
                          {slotName && points > 0 && (
                            <tspan x={x} dy="12" className="text-[10px] fill-gray-600">
                              {slotName}
                            </tspan>
                          )}
                        </text>
                      );
                    })}
                  </svg>
                </div>

                {/* Pointer */}
                <div className="absolute top-1/2 -left-1 transform -translate-y-1/2">
                  <div className="w-0 h-0 border-y-[8px] border-y-transparent border-l-[16px] border-l-red-500" />
                </div>
              </>
            )}
          </div>

          <Button
            type="button"
            onClick={handleSpin}
            disabled={!isOtpVerified || isSpinning || mustCollectPoints || !selectedSpinWheel || !hasSlots}
            className="px-8 bg-orange-600 text-white hover:bg-orange-700"
          >
            {isSpinning ? "Spinning..." : "Spin Wheel"}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Spin the wheel to see how many points you win.
            </p>
            <div className="mt-2 p-4 border rounded-md bg-gray-50 flex flex-col items-center justify-center min-h-[80px]">
              {selectedIndex !== null ? (
                <>
                  <p className="text-sm text-gray-600 mb-1">You won</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    {SEGMENTS[selectedIndex]} points
                  </p>
                  {SEGMENTS[selectedIndex] === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Better luck next time!
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No spins yet. Verify your mobile and click Spin Wheel.
                </p>
              )}
            </div>
            {selectedIndex !== null && (
              <Button
                type="button"
                className="mt-2 w-full bg-orange-600 text-white hover:bg-orange-700"
                onClick={handleCollectPoints}
                disabled={isCollecting}
              >
                {isCollecting ? "Collecting..." : "Collect Points"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

